import React, {useEffect, useMemo, useState} from "react";

import {Col, Pagination, Row, Space} from "antd";
import {SeriesPaginationQuery, useGetUserSeriesViaElasticSearchQuery} from "@mwServices/Series";

import {ClearFiltersBar} from "@components/issues-filter-drawer";
import SearchSeriesListItem from "@mwPages/search/series/SearchSeriesListItem";
import {SortIcon} from "@components/sort-icon";
import {PAGINATION_PAGES} from "@components/grid/Grid";
import usePreferences from "@hooks/usePreferences";
import SearchSeriesFiltersDrawer from "./SearchSeriesFiltersDrawer";
import SeriesGrid from "@components/grid/SeriesGrid";
import {Publisher, SeriesType} from "@mwServices/Issue";
import {scrollToThen} from "@lib/utils";
import {useWindowSize} from "@components/window-size-provider";
import {DebounceInput} from "@components/debounce-input";
import {SearchOutlined} from "@ant-design/icons";

// This defines the expected types for the params of the component.
interface SearchPageSeriesProps {
    query: string;
    setQuery: (search: string) => void;
}

const SearchPageSeries = ({
    query,
    setQuery
}: SearchPageSeriesProps) => {
    const windowDimensions = useWindowSize();
    const {itemCount, setItemCount} = usePreferences();
    const defaultFilters = useMemo(() => ({
        query: query,
        sort: 0,
    }), []);
    const [seriesParams, setIssueParams] = useState<SeriesPaginationQuery>({
        filters: {...defaultFilters},
        page: 1,
        perPage: itemCount,
    });
    const seriesQuery = useGetUserSeriesViaElasticSearchQuery(seriesParams);
    const pagination = useMemo(() => seriesQuery && seriesQuery.data && seriesQuery.data.pagination ? seriesQuery.data.pagination : null, [seriesQuery]);

    // Piece together the publisher and series types
    const [publishers, setPublishers] = useState(new Array<Publisher>());
    const [seriesTypes, setSeriesTypes] = useState(new Array<SeriesType>());
    
    // If we don't already have publishers, push them into the array
    useEffect(() => {
        if (publishers.length == 0 && seriesQuery && seriesQuery.data && seriesQuery.data.overview &&  seriesQuery.data.overview.publishers) {
            let newPublishers = new Array<Publisher>();
            seriesQuery.data.overview.publishers.forEach(publisher => {
                let keyArray = publisher.key.split('|');
                newPublishers.push({ id: Number(keyArray[0]), title: keyArray[1], count: publisher.doc_count});
            });
            setPublishers(newPublishers);
        }

        // If we don't already have series types, push those into the array
        if (seriesTypes.length == 0 && seriesQuery && seriesQuery.data && seriesQuery.data.overview &&  seriesQuery.data.overview.series_types) {
            let newSeriesTypes = new Array<SeriesType>();
            seriesQuery.data.overview.series_types.forEach(seriesType => {
                newSeriesTypes.push({ title: seriesType.key, count: seriesType.doc_count});
            });
            setSeriesTypes(newSeriesTypes);
        }
    }, [seriesQuery]);

    const showDesktopView = useMemo<boolean>(() => {
        return windowDimensions.over(799);
    }, [windowDimensions]);

    const [search, setSearch] = useState('');

    useEffect(() => {
        if(search && search !== ''){
            const newSearch = query + " #" + search.trim();
            console.log("Search isues for, " + newSearch);
            setQuery(newSearch);
        }
    }, [search]);

    return (
    <>
        <Row className="mar-5" style={{minHeight: showDesktopView ? 75 : 55}}>
            <Col span={8}  className={"flex-vertical-center"}>
                <span className={'total-issues mar-left-5'}>{pagination && pagination.total_items} Items</span>
            </Col>
            <Col span={16} className="flex-align-end flex-vertical-center">
                <DebounceInput
                    size="large"
                    placeholder={"Issue number..."}
                    className="searchInput topSearchInput"
                    prefix={<SearchOutlined/>}
                    value={search}
                    maxLength={100}
                    debounceTimeout={2000}
                    onChange={(event) => {
                        const {value} = event.target;
                        if (value !== search) setSearch(value);
                    }}
                />
            </Col>
        </Row>
        <Row className={"buttonsRow"}>
            <Col span={24} className="flex-align-end flex-vertical-center">
                <Space>                        
                    <SearchSeriesFiltersDrawer
                        filters={seriesParams.filters}
                        setFilters={(filters) => setIssueParams({...seriesParams, page: 1, filters})}
                        publishers={publishers}
                        seriesTypes={seriesTypes}
                    />

                    {pagination && (
                        <Pagination
                            current={pagination.current_page}
                            pageSize={pagination.per_page}
                            total={pagination.total_items}
                            onChange={(page) => {
                                if(seriesParams && seriesParams.page !== page){
                                    setIssueParams({...seriesParams, page: page});
                                }
                            }}
                            simple
                        />
                    )}
                </Space>
            </Col>
        </Row>

        <ClearFiltersBar
            filters={seriesParams.filters}
            defaultFilters={defaultFilters}
            issueCount={seriesQuery.data ? seriesQuery.data.overview.total : 0}
            onClear={() => {
                setIssueParams({...seriesParams, page: 1, filters: defaultFilters});
            }}
        />
                
        {windowDimensions.isLargeScreen && (
            <Row className="list-view-header">
                <Col span={2}>{/* For book cover spacing */}</Col>
                <Col span={2}>{/* gap col */}</Col>
                <Col span={6}>
                    <div className="flex padding-left-standard">
                        Series
                        <SortIcon
                            sort={seriesParams.filters.sort}
                            sortAsc={1}
                            sortDesc={2}
                            onChange={(sort) => setIssueParams({...seriesParams,page: 1,  filters: {...seriesParams.filters, sort}})}
                        />
                    </div>
                </Col>
                <Col span={3}>
                    <div className="flex-align-center">
                        Year
                        <SortIcon
                            sort={seriesParams.filters.sort}
                            sortAsc={8}
                            sortDesc={7}
                            onChange={(sort) => setIssueParams({...seriesParams, page: 1, filters: {...seriesParams.filters, sort}})}

                        />
                    </div>
                </Col>
                <Col span={3}>
                    <div className="text-center">Issue(s)</div>
                </Col>
                <Col span={3}>
                    <div className="text-center">Publisher</div>
                </Col>
                <Col span={3}>
                    <div className="text-center">Series Type</div>
                </Col>
                <Col span={1}>
                    {/* <div className="text-center">Actions</div> */}
                </Col>
            </Row>
        )}

        <SeriesGrid
            isLoading={seriesQuery.isLoading || seriesQuery.isFetching}
            data={seriesQuery.data}
            noDataMessage="Try Rephrasing Your Search Or Different Filters And Try Again!"
            SeriesRender={(props) =>
                <SearchSeriesListItem
                    item={props.item}
                    onClick={props.onClick}
                />}
            getItemProps={(item) => ({
                key: item.id,
            })}
            paginationType={PAGINATION_PAGES}
            onPaginationChange={(page => {
                if(seriesParams && seriesParams.page !== page){
                    scrollToThen({top: 0, behavior: 'smooth'}, () => {
                        setIssueParams({...seriesParams, page: page});
                    });
                }
            })}
            onPaginationSizeChange={(current: number, perPage: number) => {
                if(seriesParams && seriesParams.perPage !== perPage){
                    scrollToThen({top: 0, behavior: 'smooth'}, () => {
                        setIssueParams({...seriesParams,
                            page: 1,
                            perPage: perPage,
                        });
                        setItemCount(perPage);
                    });
                }
            }}
        />
    </>
    );
};

export default SearchPageSeries;